import { QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError, getAuthToken, setAuthToken } from "./api";
import { revokeAllAccountPhotos } from "@/features/account/useAccountPhoto";
import { clearUploadQueueStorage } from "@/features/coach/components/useUploadQueue";
import { revokeAllThumbnails } from "@/features/coach-review/hooks/useThumbnail";
import { revokeAllPlayerPhotos } from "@/features/player-photos/usePlayerPhoto";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

/** Default TanStack Query config.
 *
 *   - 30 s stale time: most coach surfaces tolerate slightly stale
 *     reads between active actions; the per-query `staleTime` override
 *     is fine for fast-moving surfaces (admin performance, live status).
 *   - retry: skip 4xx (auth / not-found / validation are user errors,
 *     retrying would just hammer the API). Network/5xx get one retry.
 *   - refetchOnWindowFocus: enabled — focusing the tab is the cheapest
 *     "is this still fresh?" signal we get.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) {
        applyAuthExpiredBoundary();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
});

/** Apply the client-side auth boundary after the backend rejects the
 * stored session/token.
 *
 * This is intentionally shared by the global query error handler and
 * `/api/me`'s explicit 401 effect. A backend restart can invalidate
 * the local token while the SPA still has a warm `/api/me` result; in
 * that case the first request to discover the expired session might be
 * any authenticated query, not `/api/me` itself. The boundary clears
 * local identity, scope, private caches, thumbnail blobs, and persisted
 * upload rows in one place.
 *
 * Idempotent: a single render can produce several 401s in the same tick
 * (e.g. `me` + a private query mounted in parallel), each one routing
 * through the global `queryCache.onError` handler. The first call wipes
 * authenticated state; subsequent calls only need to keep the
 * `hydrating` flag flipped to `false` so the shells (`HubRedirect`,
 * protected route guards) can render their anonymous redirect. The
 * private-cache evictions and blob revocations are skipped on the
 * idempotent path since nothing remains to clear.
 *
 * Cache mechanism: the full path uses `removeAuthenticatedFromCache`
 * (cache eviction via `queryClient.removeQueries`), not
 * `queryClient.invalidateQueries`. Eviction guarantees the next
 * subscriber goes into `pending` and waits for a fresh fetch;
 * invalidation marks data stale but leaves it in the cache, allowing
 * a synchronous read of the previous user's data across the auth
 * boundary. Privacy isolation needs eviction, not staleness. See
 * `useMe.ts` (the explicit /api/me 401 effect) for the parallel
 * docstring.
 *
 * #231 — before this idempotent branch existed, a never-signed-in user's
 * very first `/api/me` 401 hit the "already anonymous" early-return
 * BEFORE `applyAnonymous()` ran, so the auth store's `hydrating: true`
 * default never flipped. `HubRedirect` gates on `hydrating`, so the
 * anonymous → `/login` redirect from `/hub` never fired. Always calling
 * `applyAnonymous()` (it's idempotent on the user/profile/membership
 * fields when they're already null) closes that gap without affecting
 * signed-in users — they have `user !== null` and don't take this path.
 */
export function applyAuthExpiredBoundary(): void {
  const alreadyAnonymous =
    useAuthStore.getState().user === null && getAuthToken() === null;
  if (alreadyAnonymous) {
    // Cheap path — just ensure `hydrating` is cleared so anonymous-aware
    // shells can render. `applyAnonymous` sets `hydrating: false` and is
    // a no-op for the other fields when they're already null/[].
    useAuthStore.getState().applyAnonymous();
    return;
  }
  setAuthToken(null);
  useAuthStore.getState().applyAnonymous();
  useScopeStore.getState().clear();
  removeAuthenticatedFromCache();
  revokeAllThumbnails();
  revokeAllPlayerPhotos();
  revokeAllAccountPhotos();
  clearUploadQueueStorage();
}

/** Apply the client-side boundary for an intentional user sign-out.
 *
 * This is slightly stricter than `applyAuthExpiredBoundary()`: the 401
 * boundary preserves the `me` query so an already-anonymous tab does not
 * get trapped in a `/api/me` refetch loop, but an explicit logout must
 * clear the previous successful `/api/me` payload. Otherwise `useMe()` can
 * reconcile that stale payload back into the auth store after the user
 * clicks Log out.
 */
export function applyExplicitLogoutBoundary(): void {
  setAuthToken(null);
  useAuthStore.getState().applyAnonymous();
  useScopeStore.getState().clear();
  removeAuthenticatedFromCache();
  queryClient.setQueryData(unscopedKey("me"), null);
  queryClient.removeQueries({ queryKey: unscopedKey("me"), exact: true });
  revokeAllThumbnails();
  revokeAllPlayerPhotos();
  revokeAllAccountPhotos();
  clearUploadQueueStorage();
}

/**
 * Scope-aware query key helpers.
 *
 * Convention: every scoped query key is a 4-element tuple
 *   [domain, clubId-or-null, teamId-or-null, seasonId-or-null, ...rest]
 * so that switching club/scope auto-invalidates the cache for everything
 * in the previous scope (TanStack Query treats key tuples as structurally
 * equal — different tuples are different caches). `clubId` leads so a
 * shared .tktic.com cookie cannot replay another club's cache.
 *
 * Two flavors:
 *   - `useScopedKey("domain", extra?)` — React HOOK. Subscribes to
 *     both `teamId` and `seasonId` from the scope store via Zustand
 *     selectors, so the component re-renders whenever either changes.
 *     ALWAYS use this inside `useQuery({ queryKey: useScopedKey(...) })`.
 *     The `use` prefix marks it as a hook so the react-hooks ESLint
 *     rule (when we add it) will catch misuse — and so a non-React
 *     caller has an obviously wrong-named function to reach for.
 *   - `scopedKeyOf(scope, "domain", extra?)` — non-hook snapshot.
 *     For mutation callbacks, invalidation predicates, and anywhere a
 *     hook call would throw "Invalid hook call".
 *
 * Examples:
 *   queryKey: useScopedKey("matches")
 *     → ["matches", "team-riverside", "season-riverside-2026"]
 *   queryKey: useScopedKey("coaching-notes", { match_id: "..." })
 *     → ["coaching-notes", "team-…", "season-…", { match_id: "..." }]
 *   queryKey: unscopedKey("settings-public")
 *     → ["settings-public"]
 *
 * For invalidation across all queries in the current scope after a
 * mutation, prefer `invalidateScope()` over per-key invalidation —
 * cheaper to maintain as the surface grows.
 */

export type ScopedQueryKey =
  | readonly [
      domain: string,
      clubId: string | null,
      teamId: string | null,
      seasonId: string | null,
    ]
  | readonly [
      domain: string,
      clubId: string | null,
      teamId: string | null,
      seasonId: string | null,
      extra: Record<string, unknown>,
    ];

export type UnscopedQueryKey = readonly [domain: string, extra?: Record<string, unknown>];

/** Build a scope-aware key tuple from the CURRENT scope (REACT HOOK).
 *
 * Must be called from inside a React render. Subscribes to BOTH
 * `teamId` and `seasonId` via Zustand selectors so the consuming
 * component re-renders whenever either changes — a previous version
 * called `useScopeStore.getState()` here (unsubscribed read), which
 * meant a season swap within the same team would NOT advance the
 * query key.
 *
 * For non-React callers (mutation onSuccess, invalidation predicates,
 * any context where a hook call would throw "Invalid hook call"),
 * use `scopedKeyOf({ teamId, seasonId }, "domain")` instead.
 */
export function useScopedKey(
  domain: string,
  extra?: Record<string, unknown>,
): ScopedQueryKey {
  // club_id is the FIRST partition dimension (multi-club): a shared
  // .tktic.com cookie must never replay another club's cached data after
  // a subdomain switch. Club lives in the auth store (it's part of
  // identity-on-this-host, not team/season scope), so read it here.
  const clubId = useAuthStore((s) => s.currentClub?.id ?? null);
  const teamId = useScopeStore((s) => s.teamId);
  const seasonId = useScopeStore((s) => s.seasonId);
  if (extra && Object.keys(extra).length > 0) {
    return [domain, clubId, teamId, seasonId, extra] as const;
  }
  return [domain, clubId, teamId, seasonId] as const;
}

/** Non-hook variant for code outside a React render. Pass an explicit
 * scope snapshot (typically from `useScopeStore.getState()`). The club id
 * defaults to the current auth-store club when omitted. */
export function scopedKeyOf(
  scope: { teamId: string | null; seasonId: string | null; clubId?: string | null },
  domain: string,
  extra?: Record<string, unknown>,
): ScopedQueryKey {
  const clubId =
    scope.clubId !== undefined ? scope.clubId : useAuthStore.getState().currentClub?.id ?? null;
  if (extra && Object.keys(extra).length > 0) {
    return [domain, clubId, scope.teamId, scope.seasonId, extra] as const;
  }
  return [domain, clubId, scope.teamId, scope.seasonId] as const;
}

/** Build an unscoped key — for /api/me, /api/settings/public, admin
 * routes, etc. */
export function unscopedKey(
  domain: string,
  extra?: Record<string, unknown>,
): UnscopedQueryKey {
  return extra && Object.keys(extra).length > 0
    ? ([domain, extra] as const)
    : ([domain] as const);
}

/** Cache discriminator for ANONYMOUS public data that varies per club.
 *
 * Anonymous pages have no `/api/me` (so no `currentClub` in the store),
 * yet `/api/settings` branding and the public team/match lists differ per
 * club. The host is a safe cache *partition* key here — the server still
 * re-resolves the club from the Host header on the wire; this only stops a
 * warm club-A cache from rendering on club-B after a subdomain switch
 * (which a shared `.tktic.com` cookie/cache would otherwise allow). */
export function publicClubKey(): string {
  return typeof window !== "undefined" ? window.location.host : "";
}

/** Host-partitioned key for the shared anonymous `app-settings` branding
 * cache. Use everywhere `unscopedKey("app-settings")` was used so club A's
 * branding never bleeds into club B. */
export function publicAppSettingsKey(): UnscopedQueryKey {
  return unscopedKey("app-settings", { host: publicClubKey() });
}

/** Host-partitioned key for ANY anonymous public domain that varies per club.
 * Folds the host into the key so a warm club-A cache can't render on club-B
 * after a subdomain switch. Use this — never a bare `unscopedKey(domain, ...)`
 * — for every per-club anonymous read.
 *
 * Namespace policy: use this only for data that is identical for anonymous
 * and signed-in viewers. Auth-sensitive team descriptors, match lists, and
 * live status use `useViewerHostKey()` instead. `app-settings` uses the
 * dedicated `publicAppSettingsKey()`; both helpers are exercised by the
 * regression tests in `queryClient.test.ts`.
 *
 * Two invariants this preserves (do not break):
 *   1. The domain string stays at `key[0]` (host is folded into the trailing
 *      object at `key[1]`), so a bare `unscopedKey(domain)` invalidation from a
 *      mutation still PREFIX-matches the host-keyed reader entry — that is why
 *      mutation invalidators keep using `unscopedKey(domain)` and don't need to
 *      enumerate host/slug variants (#566).
 *   2. `key[0]` being the domain keeps these entries recognized by
 *      `PUBLIC_UNSCOPED_CACHE_DOMAINS`, so they're preserved across the
 *      sign-out boundary rather than evicted.
 */
export function publicHostKey(
  domain: string,
  extra?: Record<string, unknown>,
): UnscopedQueryKey {
  return unscopedKey(domain, { ...(extra ?? {}), host: publicClubKey() });
}

/** Host- and viewer-partitioned key for reads whose response can include
 * private club data when the current session is authorized.
 *
 * The server remains the authorization boundary. This discriminator only
 * prevents a warm response for user A from rendering for user B (or an
 * anonymous viewer) in the same browser tab. These domains are deliberately
 * NOT in `PUBLIC_UNSCOPED_CACHE_DOMAINS`, so logout evicts them entirely.
 * Keep the domain at key[0] so existing bare-domain mutation invalidations
 * continue to prefix-match every host/viewer variant.
 */
export function viewerHostKey(
  domain: string,
  viewerId: string | null,
  clubId: string | null,
  extra?: Record<string, unknown>,
): UnscopedQueryKey {
  return unscopedKey(domain, {
    ...(extra ?? {}),
    host: publicClubKey(),
    club: clubId,
    viewer: viewerId ?? "anonymous",
  });
}

/** React-hook variant of `viewerHostKey`.
 *
 * While `/api/me` is unresolved, the key uses a non-anonymous sentinel. A
 * viewer page should still wait while its first `useMe()` probe is pending,
 * but after any terminal answer it may proceed without caching a potentially
 * credentialed response in the anonymous partition. Pages gate that first
 * probe with `isPending && !isFetched`: a later 60-second `/api/me` refetch
 * must never unmount an already-rendered match or live player. Success/401
 * advances the store and therefore advances this key to a user id or
 * `anonymous`; a 5xx/network failure stays in this isolated sentinel key.
 */
export function useViewerHostKey(
  domain: string,
  extra?: Record<string, unknown>,
): UnscopedQueryKey {
  const hydrating = useAuthStore((s) => s.hydrating);
  const viewerId = useAuthStore((s) =>
    hydrating ? "__auth_unresolved__" : (s.user?.id ?? null),
  );
  const clubId = useAuthStore((s) =>
    hydrating ? null : (s.currentClub?.id ?? null),
  );
  return viewerHostKey(domain, viewerId, clubId, extra);
}

const VIEWER_TEAM_CACHE_DOMAINS = [
  "public-team",
  "public-matches",
  "team-live-status",
  "live-status-team",
] as const;

/** Evict team reads after an HttpOnly guest credential changes.
 *
 * Guest identity cannot be included in a JavaScript query key, so claiming a
 * new link must remove prior anonymous/guest responses before navigation.
 */
export function removeViewerTeamCaches(): void {
  for (const domain of VIEWER_TEAM_CACHE_DOMAINS) {
    queryClient.removeQueries({ queryKey: unscopedKey(domain) });
  }
}

/** Invalidate every scoped query in the current scope. Use after
 * mutations that may touch multiple domain caches (e.g. saving a
 * scope switch).
 *
 * Match is structural — any tuple whose 2nd/3rd elements match the
 * current scope is in scope for invalidation. `invalidateQueries`
 * marks the data stale and triggers a refetch on next observe —
 * appropriate for "we just changed state" freshness, but NOT for
 * privacy isolation (the old data stays in the cache and is returned
 * synchronously while the refetch is in flight). For the auth
 * boundary, use `removeAuthenticatedFromCache()` instead.
 */
export async function invalidateScope(): Promise<void> {
  const { teamId, seasonId } = useScopeStore.getState();
  const clubId = useAuthStore.getState().currentClub?.id ?? null;
  await queryClient.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      if (key.length < 4) return false;
      return key[1] === clubId && key[2] === teamId && key[3] === seasonId;
    },
  });
}

/** Evict EVERY scoped query from the cache, regardless of scope.
 *
 * **Use this on auth boundaries (sign-out, 401 from /api/me)** rather
 * than `invalidateQueries`. The difference is privacy-shaped:
 *
 *   - `invalidateQueries` flags entries as stale → background refetch,
 *     but the cached data is still returned synchronously to any
 *     observer that subscribes in the meantime. After a logout +
 *     re-login in the same tab where the next user has the SAME
 *     `[domain, teamId, seasonId]` tuple (common: shared lab machine,
 *     family laptop), the new user can momentarily see the previous
 *     user's coach/admin data while the refetch is in flight.
 *   - `removeQueries` evicts entries entirely → subscribers go into
 *     `pending` state and have to wait for the new fetch. No stale
 *     data is ever served across the auth boundary.
 *
 * Invalidation is for freshness; removal is for privacy isolation.
 */
export function removeAllScopedFromCache(): void {
  queryClient.removeQueries({
    predicate: (q) => (q.queryKey as unknown[]).length >= 4,
  });
}

/** Unscoped domains that are safe to keep across sign-out because they
 * are public-anonymous data already rendered on public pages. Everything
 * else without a scope tuple is treated as authenticated/private and is
 * evicted on auth boundaries.
 */
const PUBLIC_UNSCOPED_CACHE_DOMAINS = new Set([
  "app-settings",
  "settings-public",
  "public-teams",
  "live-status",
  "live-status-season-cta",
  "public-team-statuses",
]);

/** Unscoped domains preserved across the auth boundary even though they
 * carry authenticated data. The only member is the `me` probe itself:
 * its error IS the auth boundary's terminal answer ("you are anonymous
 * now"), so re-evicting it on every boundary fire causes the active
 * `useMe` observer to refetch → 401 → boundary → recursion.
 * `applyAnonymous()` on the auth store has already cleared identity for
 * non-React readers; the cache entry settles on its error and stops
 * driving requests.
 */
const AUTH_BOUNDARY_PRESERVED_DOMAINS = new Set(["me"]);

/** Evict every authenticated/private query from the cache.
 *
 * This includes all scoped queries AND private unscoped admin/account
 * domains such as `admin-users`, `admin-settings`. Public season/game/
 * index/live caches are intentionally retained so signing out from a public
 * page doesn't blank the page; they contain only anonymous-safe data and
 * will still refetch when their normal stale/refocus rules fire. Team
 * descriptors, match lists, and per-team live status are evicted because
 * those endpoints may return private data to an authorized viewer.
 *
 * The `me` probe is also preserved — see `AUTH_BOUNDARY_PRESERVED_DOMAINS`.
 */
export function removeAuthenticatedFromCache(): void {
  queryClient.removeQueries({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      if (key.length >= 4) return true;
      const domain = typeof key[0] === "string" ? key[0] : "";
      if (AUTH_BOUNDARY_PRESERVED_DOMAINS.has(domain)) return false;
      return !PUBLIC_UNSCOPED_CACHE_DOMAINS.has(domain);
    },
  });
}
